/**
 * Single source of truth for the spa's NAP (name / address / phone) and the
 * other business facts that have to stay byte-identical everywhere.
 *
 * Why this exists: the same address, phone and email were previously hardcoded
 * in six places (App.tsx footer, Contact, BookingConfirmation, the consent form,
 * the receipt header, and public/index.html's JSON-LD) and had already drifted —
 * the email domain was wrong, and the geo coordinates in the LocalBusiness
 * schema disagreed with the ones the crew time-clock geofence uses. Inconsistent
 * NAP is a direct local-SEO and AI-citation problem: Google, Apple Maps, Yelp and
 * the LLM crawlers all cross-check these strings against each other.
 *
 * public/index.html's JSON-LD block cannot import this file (it must stay static
 * so non-JS crawlers see it), so when you change anything here, update that block
 * to match in the same commit.
 */

export const BUSINESS_NAME = 'Healing Lotus Skin Spa';

/** Shorter form used in body copy and the receipt header. */
export const BUSINESS_SHORT_NAME = 'Healing Lotus Spa';

export const SITE_URL = 'https://lotushealingspa.com';

export const ADDRESS = {
  street: '4222 Trinity Mills Rd Suite 120',
  streetLine1: '4222 Trinity Mills Rd',
  streetLine2: 'Suite 120',
  city: 'Dallas',
  region: 'TX',
  postalCode: '75287',
  country: 'US',
} as const;

/** "4222 Trinity Mills Rd Suite 120, Dallas, TX 75287" */
export const ADDRESS_ONE_LINE = `${ADDRESS.street}, ${ADDRESS.city}, ${ADDRESS.region} ${ADDRESS.postalCode}`;

/** "Dallas, TX 75287" */
export const ADDRESS_CITY_LINE = `${ADDRESS.city}, ${ADDRESS.region} ${ADDRESS.postalCode}`;

export const PHONE_DISPLAY = '+1 (972) 998-0431';
export const PHONE_E164 = '+19729980431';
export const PHONE_HREF = `tel:${PHONE_E164}`;

export const EMAIL = 'info@lotushealingspa.com';
export const EMAIL_HREF = `mailto:${EMAIL}`;

/**
 * Storefront coordinates, taken from Google Maps by the owner (2026-09-04).
 *
 * Every value this repo previously held was wrong, in different directions:
 *   - public/index.html JSON-LD + the Contact page map embed: 33.0553 / -96.8306 (~7.5 km north)
 *   - utils/geolocation.ts crew time-clock geofence:          33.0213 / -96.8386 (~3.7 km north)
 *   - an interim geocode from OpenStreetMap/Nominatim:        32.9879  / -96.8388 (~100 m west)
 *
 * This constant is deliberately NOT wired into utils/geolocation.ts. That file
 * centres the crew clock-in geofence, and moving it is a change to time-clock
 * behaviour, not an SEO change - see documents/prerender-and-seo-2026-09.md.
 */
export const GEO = {
  latitude: 32.9876004,
  longitude: -96.8377287,
} as const;

export const MAPS_URL =
  'https://maps.google.com/?q=4222+Trinity+Mills+Rd+Suite+120,+Dallas,+TX+75287';

/**
 * Opening hours. Monday–Saturday share one window; Sunday differs.
 * Keep in sync with the `openingHoursSpecification` array in public/index.html.
 */
export const HOURS = {
  weekdays: { label: 'Monday - Friday', display: '10:00 AM - 7:00 PM', opens: '10:00', closes: '19:00' },
  saturday: { label: 'Saturday', display: '10:00 AM - 7:00 PM', opens: '10:00', closes: '19:00' },
  sunday: { label: 'Sunday', display: '9:30 AM - 7:30 PM', opens: '09:30', closes: '19:30' },
} as const;

export const SOCIAL = {
  instagram: 'https://www.instagram.com/healinglotuslian',
  tiktok: 'https://www.tiktok.com/@ladyfox125',
  facebook: 'https://www.facebook.com/share/1G3Eq5Lvmw',
} as const;

/**
 * Realistic catchment, not just the mailing city. The spa sits on Trinity Mills
 * Rd in Far North Dallas, on the Addison/Carrollton line — "Dallas" alone points
 * local intent at downtown, which is ~20 miles away.
 */
export const AREA_SERVED = [
  'Far North Dallas',
  'Addison',
  'Carrollton',
  'Farmers Branch',
  'Plano',
  'Frisco',
] as const;
